import { z } from 'zod'
import { MarketingChoice } from './marketing.js'
import {
  ArtifactClass,
  CheckoutProviderState,
  CheckoutReconciliationState,
  CheckoutReturnAction,
  CheckoutUnresolvedReason,
  DeliveryMode,
  DeliveryState,
  EnvelopeState,
  LocaleCode,
  ParticipantKind,
  PlanTier,
  PurchaseProvider,
  PurchaseType,
  RecipientState,
  RoutingMode,
  SenderOrder,
} from './enums.js'
import { AuditEventType } from './entities.js'
import { MCP_AUDIT_MAX_FOLD_EVENTS } from './mcp-reads.js'
import { HASH_STRING_PATTERN, SHA256_HEX_PATTERN } from './version.js'
import { AttachmentManifest, AttachmentOwner, AttachmentRequest } from './attachment.js'
import { MetricResultEnvelope } from './metrics.js'
import { ReminderPolicy } from './manifest.js'

const IsoDateTime = z.string().datetime()
const HashString = z.string().regex(HASH_STRING_PATTERN)

/**
 * Core API contracts — PRD §13.1/§13.3/§13.5, B7.
 *
 * Endpoint names are illustrative; the shapes below are the versioned request/response
 * contracts the Worker validates against. Every mutation carries a `client_request_id`
 * so the server can enforce §13.4 idempotency (key = account + operation + request id,
 * with the canonical request hash compared separately).
 */

export const API_VERSION = 'v1'

const ClientRequestId = z.string().min(8).max(128)
const Email = z.email().max(320)

/* --------------------------------- common --------------------------------- */

export const CursorPage = z
  .object({
    cursor: z.string().max(512).optional(),
    limit: z.number().int().positive().max(100),
  })
  .strict()
export type CursorPage = z.infer<typeof CursorPage>

export const PageInfo = z
  .object({
    next_cursor: z.string().max(512).nullable(),
    has_more: z.boolean(),
  })
  .strict()
export type PageInfo = z.infer<typeof PageInfo>

/** Short-lived signed upload authority for client-encrypted bytes (SEC-04). */
export const UploadGrant = z
  .object({
    object_key: z.string().min(1).max(512),
    url: z.string().min(1).max(2048),
    expires_at: IsoDateTime,
    max_bytes: z.number().int().positive(),
    required_hash: HashString.optional(),
  })
  .strict()
export type UploadGrant = z.infer<typeof UploadGrant>

export const DownloadGrant = z
  .object({
    object_key: z.string().min(1).max(512),
    url: z.string().min(1).max(2048),
    expires_at: IsoDateTime,
  })
  .strict()
export type DownloadGrant = z.infer<typeof DownloadGrant>

/* ---------------------------------- auth ---------------------------------- */

export const AuthMethod = z.enum(['apple', 'google', 'email_otp'])
export type AuthMethod = z.infer<typeof AuthMethod>

export const AuthStartRequest = z
  .object({
    client_request_id: ClientRequestId,
    method: AuthMethod,
    email: Email.optional(),
    /** Opaque continuation the client resumes after verification (IDN-01/10). */
    continuation: z.string().max(512).optional(),
    return_action: z.string().max(120).optional(),
    /** The sign-in screen's marketing checkbox; applied only if this sign-in succeeds. */
    marketing: MarketingChoice.optional(),
    /**
     * SHA-256 (hex) of a secret the starting browser or app keeps (DEV-022). A challenge started
     * with one completes only when the secret is presented, so the email's sign-in link and an
     * Apple/Google redirect sign in only the device that asked. Required for Apple/Google.
     */
    binding_hash: z
      .string()
      .regex(/^[0-9a-f]{64}$/)
      .optional(),
    /** Where an Apple/Google sign-in returns: this web origin (default) or the installed app. */
    return_to: z.enum(['web', 'app']).optional(),
  })
  .strict()
export type AuthStartRequest = z.infer<typeof AuthStartRequest>

export const AuthStartResponse = z
  .object({
    challenge_id: z.string().min(1).max(128),
    method: AuthMethod,
    expires_at: IsoDateTime,
    /** Provider redirect for OAuth methods; absent for email OTP. */
    authorization_url: z.string().max(2048).optional(),
  })
  .strict()
export type AuthStartResponse = z.infer<typeof AuthStartResponse>

export const AuthCompleteRequest = z
  .object({
    client_request_id: ClientRequestId,
    challenge_id: z.string().min(1).max(128),
    /**
     * The email code, or for Apple/Google the authorization code the provider callback returned
     * (those are long opaque strings, hence the bound).
     */
    code: z.string().min(4).max(2048).optional(),
    continuation: z.string().max(512).optional(),
    /** The secret whose hash the start sent as `binding_hash`; required when it sent one. */
    binding: z.string().min(32).max(128).optional(),
    /**
     * How the session is handed back (§10.2.1). `cookie` (the default) sets the HttpOnly session
     * cookie a browser sends on its own; `bearer` is for the installed app, which is not a browser
     * origin the cookie policy trusts: the token comes back in the body, the app keeps it and
     * sends it as `Authorization: Bearer`, and no cookie is set.
     */
    session_transport: z.enum(['cookie', 'bearer']).optional(),
  })
  .strict()
export type AuthCompleteRequest = z.infer<typeof AuthCompleteRequest>

export const AuthCompleteResponse = z
  .object({
    user_id: z.string().min(1).max(128),
    session_expires_at: IsoDateTime,
    resumed_continuation: z.string().max(512).nullable(),
    /** Present only when the request asked for the `bearer` transport. */
    session_token: z.string().min(1).max(512).optional(),
  })
  .strict()
export type AuthCompleteResponse = z.infer<typeof AuthCompleteResponse>

/**
 * Apple/Google proved an identity whose verified email already belongs to an account (PRD §10.2,
 * IDN-03). Nothing is merged: a code went to that email, and completing this new email challenge
 * with it signs in to that account and links the provider in the same step. No session yet.
 */
export const AuthLinkRequired = z
  .object({
    link_required: z.literal(true),
    provider: z.enum(['apple', 'google']),
    challenge_id: z.string().min(1).max(128),
    email: Email,
    expires_at: IsoDateTime,
  })
  .strict()
export type AuthLinkRequired = z.infer<typeof AuthLinkRequired>

/** What `POST /v1/auth/complete` answers: a session, or the link step above. */
export const AuthCompleteAnswer = z.union([AuthCompleteResponse, AuthLinkRequired])
export type AuthCompleteAnswer = z.infer<typeof AuthCompleteAnswer>

/**
 * The sign-in methods this deployment offers (`GET /v1/auth/methods`). A method whose provider
 * registration is not configured is `false`, so the sign-in screen never shows a button that can
 * only fail (fail closed until configured).
 */
export const AuthMethodsResponse = z
  .object({
    email_otp: z.boolean(),
    apple: z.boolean(),
    google: z.boolean(),
    passkey: z.boolean(),
  })
  .strict()
export type AuthMethodsResponse = z.infer<typeof AuthMethodsResponse>

/* -------------------------------- passkeys -------------------------------- */

/**
 * A WebAuthn ceremony's options (DEV-023). `ceremony` is the opaque handle of the server-held
 * challenge; the client returns it with the authenticator's answer. `options` is the standard
 * `PublicKeyCredentialCreationOptionsJSON` / `PublicKeyCredentialRequestOptionsJSON`.
 */
export const PasskeyOptionsResponse = z
  .object({
    ceremony: z.string().min(16).max(512),
    options: z.record(z.string(), z.unknown()),
  })
  .strict()
export type PasskeyOptionsResponse = z.infer<typeof PasskeyOptionsResponse>

/** The authenticator's answer (`RegistrationResponseJSON` / `AuthenticationResponseJSON`). */
const WebAuthnResponse = z.record(z.string(), z.unknown())

/**
 * A passkey sign-in's options request. Like an email or Apple/Google start (DEV-022), it sends
 * only the SHA-256 (hex) of a secret the browser keeps; the ceremony it opens completes only when
 * that secret is presented, so a ceremony and assertion taken elsewhere cannot be finished here.
 */
export const PasskeySignInOptionsRequest = z
  .object({
    binding_hash: z.string().regex(/^[0-9a-f]{64}$/),
  })
  .strict()
export type PasskeySignInOptionsRequest = z.infer<typeof PasskeySignInOptionsRequest>

export const PasskeySignInRequest = z
  .object({
    client_request_id: ClientRequestId,
    ceremony: z.string().min(16).max(512),
    response: WebAuthnResponse,
    /** The secret whose hash the options request sent as `binding_hash`. */
    binding: z.string().min(32).max(128),
    continuation: z.string().max(512).optional(),
    session_transport: z.enum(['cookie', 'bearer']).optional(),
  })
  .strict()
export type PasskeySignInRequest = z.infer<typeof PasskeySignInRequest>

export const PasskeyRegisterRequest = z
  .object({
    client_request_id: ClientRequestId,
    ceremony: z.string().min(16).max(512),
    response: WebAuthnResponse,
    name: z.string().trim().min(1).max(80).optional(),
  })
  .strict()
export type PasskeyRegisterRequest = z.infer<typeof PasskeyRegisterRequest>

export const PasskeySummary = z
  .object({
    id: z.string().min(1).max(128),
    name: z.string().max(80).nullable(),
    created_at: IsoDateTime.nullable(),
    /** `multiDevice` passkeys sync through the person's password manager. */
    device_type: z.string().max(40),
    backed_up: z.boolean(),
  })
  .strict()
export type PasskeySummary = z.infer<typeof PasskeySummary>

export const PasskeyListResponse = z.object({ items: z.array(PasskeySummary) }).strict()
export type PasskeyListResponse = z.infer<typeof PasskeyListResponse>

/**
 * Credit snapshot — PRD §2.6. `posted` is the sum of the append-only ledger deltas, `reserved`
 * the sum of the held reservations, and `available` their difference; reservations are never
 * debits and a new reservation is only permitted while the result stays nonnegative.
 */
export const CreditSnapshot = z
  .object({
    posted: z.number().int().nonnegative(),
    reserved: z.number().int().nonnegative(),
    available: z.number().int().nonnegative(),
    /**
     * Lifetime grants and credit-pack grants never expire (§2.5). The flag exists so the client
     * renders the non-expiring wording from the server fact instead of hard-coding the rule.
     */
    non_expiring: z.boolean(),
  })
  .strict()
export type CreditSnapshot = z.infer<typeof CreditSnapshot>

/**
 * Derived Lifetime state — PRD §2.9. `active` is derived from *all* valid underlying purchase
 * transactions (never from the last provider event or one platform), and `purchase_txn_id`
 * names the verified transaction currently carrying the entitlement.
 */
export const LifetimeEntitlementState = z
  .object({
    active: z.boolean(),
    purchase_txn_id: z.string().min(1).max(128).nullable(),
    granted_at: IsoDateTime.nullable(),
    revoked_at: IsoDateTime.nullable(),
  })
  .strict()
export type LifetimeEntitlementState = z.infer<typeof LifetimeEntitlementState>

/** Active-request usage against the tier limit; both come from the register (§14.6). */
export const ActiveRequestState = z
  .object({
    used: z.number().int().nonnegative(),
    limit: z.number().int().positive(),
    tier: PlanTier,
  })
  .strict()
export type ActiveRequestState = z.infer<typeof ActiveRequestState>

/** The entitlement half of the account snapshot, shared by `/v1/me` and Restore (§2.9). */
export const MeEntitlementSnapshot = z
  .object({
    plan: PlanTier,
    lifetime: LifetimeEntitlementState,
    credits: CreditSnapshot,
    active_requests: ActiveRequestState,
    /**
     * When the service derived this snapshot from its own records, on its own clock (§2.7, S6.3).
     * A client may cache the snapshot for already-owned offline convenience, and this is the fact
     * that lets it age honestly instead of remembering a balance forever; a remote action always
     * asks the service again.
     */
    derived_at: IsoDateTime,
    /**
     * The snapshot is always derived from the service's verified records — never from a balance a
     * client stored. Stated as a field so no surface can present a cached number as authority.
     */
    authority: z.literal('server_records'),
  })
  .strict()
export type MeEntitlementSnapshot = z.infer<typeof MeEntitlementSnapshot>

export const MeResponse = z
  .object({
    user_id: z.string().min(1).max(128),
    primary_email_normalized: Email.optional(),
    plan: PlanTier,
    lifetime: LifetimeEntitlementState,
    credits: CreditSnapshot,
    active_requests: ActiveRequestState,
    /**
     * The same honesty fields the entitlement object carries (`MeEntitlementSnapshot`): this is
     * the surface a client caches for offline convenience, so the derivation time and the
     * statement that these numbers came from the service's own records travel with it (S6.3).
     */
    derived_at: IsoDateTime,
    authority: z.literal('server_records'),
    /**
     * Kept alongside `active_requests` because other slices and the web client already read
     * them; both are the same snapshot and can never disagree.
     */
    active_request_count: z.number().int().nonnegative(),
    active_request_limit: z.number().int().positive(),
    trial: z.enum(['AVAILABLE', 'RESERVED', 'CONSUMED']),
  })
  .strict()
export type MeResponse = z.infer<typeof MeResponse>

/**
 * Restore Purchase / Refresh Entitlement — PRD §2.9. Re-derives the entitlement from the
 * service's own verified purchase records and never mints a grant, so `restored` is `false`
 * when the account owns nothing new to recover.
 */
export const RestorePurchaseRequest = z.object({ client_request_id: ClientRequestId }).strict()
export type RestorePurchaseRequest = z.infer<typeof RestorePurchaseRequest>

/**
 * What the restore actually did (S6.3, PUR-04). The three literals are the rule, stated as data
 * rather than as copy: a restore re-derives, it never grants and it never touches a local
 * document. `provider_state` is the reconciliation result when the service asked the provider
 * (`provider_checked`) and `null` when no provider adapter is wired — the answer never claims a
 * round-trip it did not make.
 */
export const RestoreReconciliation = z
  .object({
    provider_checked: z.boolean(),
    provider_state: z.enum(['PENDING', 'PURCHASED', 'REVOKED', 'NOTHING_TO_VERIFY']).nullable(),
    grants_new_entitlement: z.literal(false),
    restores_local_documents: z.literal(false),
  })
  .strict()
export type RestoreReconciliation = z.infer<typeof RestoreReconciliation>

export const RestorePurchaseResponse = z
  .object({
    restored: z.boolean(),
    entitlement: MeEntitlementSnapshot.optional(),
    reconciliation: RestoreReconciliation,
  })
  .strict()
export type RestorePurchaseResponse = z.infer<typeof RestorePurchaseResponse>

/* -------------------------------- envelopes ------------------------------- */

export const EnvelopeParticipantSpec = z
  .object({
    role_id: z.string().min(1).max(64).optional(),
    kind: ParticipantKind,
    name: z.string().min(1).max(200),
    email: Email.optional(),
    order_index: z.number().int().nonnegative(),
    locale_code: LocaleCode.optional(),
    is_sender: z.boolean(),
  })
  .strict()
export type EnvelopeParticipantSpec = z.infer<typeof EnvelopeParticipantSpec>

/** A sender's name for one request: 1-200 characters, not only spaces. */
export const SenderDisplayName = z.string().min(1).max(200).regex(/\S/)

export const CreateEnvelopeRequest = z
  .object({
    client_request_id: ClientRequestId,
    draft_id: z.string().min(1).max(128),
    title: z.string().min(1).max(300),
    source_hash: HashString,
    source_manifest_digest: z.string().regex(/^[0-9a-f]{64}$/),
    field_manifest_digest: z.string().regex(/^[0-9a-f]{64}$/),
    participants: z.array(EnvelopeParticipantSpec).min(1).max(20),
    delivery_mode: DeliveryMode,
    routing_mode: RoutingMode,
    sender_order: SenderOrder,
    expires_in_days: z.number().int().positive().max(90).optional(),
    /** Reserve a delivery credit without consuming it (RMT-01/D04). */
    reserve_credit: z.boolean(),
    /**
     * D10 recipient attachment requests (S5.8). Lifetime only, and every request is validated
     * against the register (`attachment_allowed_types`, `attachment_max_files_per_field`,
     * `attachment_max_file_bytes`, `attachment_max_total_bytes`, `attachment_default_viewers`)
     * before any write — a Free account that configures one is refused with the exact D12
     * limitation and its draft is untouched.
     */
    attachment_requests: z.array(AttachmentRequest).max(20).optional(),
    /**
     * The name recipients see as "From …" on this request (advisor, 14 Oct 2026). The Send screen
     * prefills it from My Info and the sender can change it. It is stored with this request only,
     * never on the account, and nothing else from My Info is sent. Absent: recipients see a generic
     * "The sender".
     */
    sender_display_name: SenderDisplayName.optional(),
    /**
     * Automatic reminders (PRD §6.4, §2.1). Absent means Off. Managed delivery only: the service
     * refuses a policy on a manual request, a first reminder that is not a 1/2/3/7-day preset, a
     * repeat interval below `reminder_min_spacing_hours` and a count outside
     * 1..`reminders_per_recipient`, and refuses every policy while either register value is unset.
     */
    reminder_policy: ReminderPolicy.optional(),
    /**
     * "Send again" after a preparation that never went out: the retired envelope this request
     * replaces. The service accepts it only for the caller's own envelope in `PREPARATION_FAILED`,
     * and at most one envelope may ever replace a given one, so two copies of one restarted draft
     * can never both become requests: the second is refused with `ENVELOPE_ALREADY_REPLACED`.
     */
    replaces_envelope_id: z.string().min(1).max(128).optional(),
  })
  .strict()
export type CreateEnvelopeRequest = z.infer<typeof CreateEnvelopeRequest>

export const CreateEnvelopeResponse = z
  .object({
    envelope_id: z.string().min(1).max(128),
    state: EnvelopeState,
    expires_at: IsoDateTime,
    upload_grant: UploadGrant,
    reservation_id: z.string().min(1).max(128).nullable(),
    revision: z.number().int().nonnegative(),
    /**
     * The envelope key the sender seals this package with, derived from the deployment secret
     * and the envelope identity (`signbento:envelope-key:v1`, 32 bytes, unpadded base64url).
     *
     * It is returned here — before sealing, which happens after create and before upload — so the
     * sender seals with the same key a managed message can later put in the link's `#k=` fragment.
     * The link fragment stays the transport for the *recipient*; this is what lets the service
     * compose that fragment at send time without any key material at rest.
     */
    envelope_key: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
  })
  .strict()
export type CreateEnvelopeResponse = z.infer<typeof CreateEnvelopeResponse>

/** Owner-only recovery of the same unexpired PREPARING envelope after a lost create response. */
export const ResumePreparationResponse = CreateEnvelopeResponse.extend({
  draft_id: z.string().min(1).max(128),
  title: z.string().min(1).max(300),
  delivery_mode: DeliveryMode,
  routing_mode: RoutingMode,
  sender_order: SenderOrder,
  signing_window_days: z.number().int().positive().max(90),
  source_hash: HashString,
  source_manifest_digest: z.string().regex(/^[0-9a-f]{64}$/),
  field_manifest_digest: z.string().regex(/^[0-9a-f]{64}$/),
  participants: z.array(EnvelopeParticipantSpec).min(1).max(20),
  attachment_requests: z.array(AttachmentRequest).max(20),
  sender_display_name: SenderDisplayName.nullable(),
  /**
   * The stored automatic-reminder policy; `null` is Off. Compared on resume like the rest. Optional
   * so a response from a service that predates reminders still parses: absent is also Off, and such
   * a service refuses any create that carries a policy (the request schema is strict).
   */
  reminder_policy: ReminderPolicy.nullable().optional(),
}).strict()
export type ResumePreparationResponse = z.infer<typeof ResumePreparationResponse>

/**
 * The sender's own step — PRD §6.1, D02. A sender who signs is not a bearer of a capability
 * link; their step is an *adoption* of the frozen activation payload, recorded either inside the
 * activation transaction (`sender_order: BEFORE`) or later, once the routing chain reaches it
 * (`sender_order: AFTER`). One shape serves both timings.
 */
export const SenderAdoption = z
  .object({
    adopted: z.literal(true),
    display_name: z.string().min(1).max(200),
    adopted_at: IsoDateTime,
  })
  .strict()
export type SenderAdoption = z.infer<typeof SenderAdoption>

export const ActivateEnvelopeRequest = z
  .object({
    client_request_id: ClientRequestId,
    uploaded_hash: HashString,
    uploaded_bytes: z.number().int().positive(),
    /** Explicit acknowledgement of the frozen activation payload (D02). */
    sender_adoption: SenderAdoption.optional(),
  })
  .strict()
export type ActivateEnvelopeRequest = z.infer<typeof ActivateEnvelopeRequest>

export const SenderAdoptionRequest = z
  .object({ client_request_id: ClientRequestId, ...SenderAdoption.shape })
  .strict()
export type SenderAdoptionRequest = z.infer<typeof SenderAdoptionRequest>

export const SenderAdoptionResponse = z
  .object({
    envelope_id: z.string().min(1).max(128),
    recipient_id: z.string().min(1).max(128),
    state: RecipientState,
    envelope_state: EnvelopeState,
    adopted_at: IsoDateTime,
  })
  .strict()
export type SenderAdoptionResponse = z.infer<typeof SenderAdoptionResponse>

export const ActivateEnvelopeResponse = z
  .object({
    envelope_id: z.string().min(1).max(128),
    state: EnvelopeState,
    activated_at: IsoDateTime,
    expires_at: IsoDateTime,
    credit_effect: z
      .object({
        kind: z.enum(['none', 'trial', 'credit']),
        debited: z.number().int().nonnegative(),
        available_after: z.number().int().nonnegative(),
      })
      .strict(),
    recipients: z.array(
      z
        .object({
          recipient_id: z.string().min(1).max(128),
          capability_version: z.number().int().positive(),
          order_index: z.number().int().nonnegative(),
          state: RecipientState,
        })
        .strict(),
    ),
  })
  .strict()
export type ActivateEnvelopeResponse = z.infer<typeof ActivateEnvelopeResponse>

export const EnvelopeSummary = z
  .object({
    envelope_id: z.string().min(1).max(128),
    /** Present on the sender list for exact draft-key recovery; detail may omit it. */
    draft_id: z.string().min(1).max(128).optional(),
    title: z.string().min(1).max(300),
    state: EnvelopeState,
    delivery_mode: DeliveryMode,
    routing_mode: RoutingMode,
    created_at: IsoDateTime,
    activated_at: IsoDateTime.optional(),
    expires_at: IsoDateTime.optional(),
    completed_at: IsoDateTime.optional(),
    /**
     * Sender-visible counts derived from authoritative envelope/recipient state, never from
     * email sends or the envelope row itself (§9, ADD-029): `submitted_count`/`declined_count`
     * come from `recipients.state`.
     */
    recipient_count: z.number().int().nonnegative(),
    submitted_count: z.number().int().nonnegative(),
    declined_count: z.number().int().nonnegative(),
    /**
     * The counts that never include CC recipients (RMT-12): `required_signer_count` is the
     * external signer set the tier limits and the routing chain are about, and
     * `cc_recipient_count` is the final-read audience, which holds no signing authority.
     * `recipient_count` stays the whole participant list, so the two can be checked against it.
     */
    required_signer_count: z.number().int().nonnegative(),
    /**
     * How many of those required signers have submitted: the numerator that pairs with
     * `required_signer_count` in "X of Y signed". `submitted_count` also counts the sender's own
     * submission, so it is the wrong numerator for that progress; it stays because "any accepted
     * response" is what closes a deadline extension.
     */
    required_signer_submitted_count: z.number().int().nonnegative(),
    cc_recipient_count: z.number().int().nonnegative(),
    /**
     * Who an ACTIVE request is waiting on, so the sender's list can say "Waiting for Jane" rather
     * than only a count: the first external signer whose turn it is (by signing order), and how
     * many external signers can sign right now. Absent once nobody but the sender is up. Sender
     * list only; the sender already sees these names on the request's detail.
     */
    next_signer_name: z.string().min(1).max(200).optional(),
    waiting_signer_count: z.number().int().nonnegative().optional(),
    /**
     * The operational revision an edit must quote (S5.5, STM-10). It is bumped by each
     * operational write to the envelope (an expiry extension), never by the draft lifecycle, so
     * two sender tabs cannot silently overwrite each other.
     */
    lock_version: z.number().int().nonnegative(),
    artifact_class: ArtifactClass.optional(),
  })
  .strict()
export type EnvelopeSummary = z.infer<typeof EnvelopeSummary>

/**
 * Query for the account-scoped envelope list. `state` and `delivery_mode` are the sender-side
 * drill-down filters; both are rendered through the same predicate builder the connected
 * metrics path uses, so the list filter and a metric drill-down can never drift (API-03).
 * `draft_id` is an exact, owner-scoped recovery lookup for a lost create response (DEC-07).
 */
export const EnvelopeListQuery = z
  .object({
    cursor: z.string().max(512).optional(),
    limit: z.number().int().positive().max(100).optional(),
    state: EnvelopeState.optional(),
    delivery_mode: DeliveryMode.optional(),
    draft_id: z.string().min(1).max(128).optional(),
  })
  .strict()
export type EnvelopeListQuery = z.infer<typeof EnvelopeListQuery>

/**
 * `next_cursor` is the opaque keyset position of the next page, or `null` at the end. An
 * unknown/invalid cursor is a validation error, never a silent restart that could duplicate
 * or skip rows.
 */
export const EnvelopeListResponse = z
  .object({
    items: z.array(EnvelopeSummary),
    next_cursor: z.string().max(512).nullable(),
  })
  .strict()
export type EnvelopeListResponse = z.infer<typeof EnvelopeListResponse>

/**
 * One sender-visible request timeline entry — §3.6 ("Request detail shows timeline, recipient
 * states, observed presentation times"), §6.2/§6.3, EML-05.
 *
 * Only facts the server holds, at the instant the server recorded them:
 * - `delivered` is a provider-confirmed delivery of a managed invitation, never provider
 *   acceptance and never a manual share (the service cannot see WhatsApp, SMS or a Mail app);
 * - `viewed` is the first document presentation, not an email open and not proof of identity;
 * - `reminder_sent` is a managed reminder the provider accepted;
 * - `corrected` is the sender correcting that participant's name or email (a new capability).
 *
 * An entry names its participant only by the `recipient_id` the detail's `recipients` list
 * already exposes; no audit payload, address or device data ever travels here. A participant
 * entry recorded before that participant's latest correction is `superseded`: it belongs to the
 * previous holder, so it must not be shown under the current name.
 */
export const RequestTimelineEventType = z.enum([
  'created',
  'activated',
  'delivered',
  'viewed',
  'signed',
  'declined',
  'reminder_sent',
  'corrected',
  'completed',
  'voided',
  'expired',
])
export type RequestTimelineEventType = z.infer<typeof RequestTimelineEventType>

export const RequestTimelineEvent = z
  .object({
    type: RequestTimelineEventType,
    occurred_at: IsoDateTime,
    recipient_id: z.string().min(1).max(128).optional(),
    superseded: z.literal(true).optional(),
  })
  .strict()
export type RequestTimelineEvent = z.infer<typeof RequestTimelineEvent>

export const EnvelopeDetailResponse = z
  .object({
    envelope: EnvelopeSummary,
    recipients: z.array(
      z
        .object({
          recipient_id: z.string().min(1).max(128),
          role_id: z.string().min(1).max(64).optional(),
          kind: ParticipantKind,
          name: z.string().min(1).max(200),
          /**
           * The address on record, for the sender's own participant list. It is never part of a
           * recipient payload and never written to diagnostics (§15.5, OPS-04).
           */
          email: Email.optional(),
          state: RecipientState,
          delivery_state: DeliveryState,
          order_index: z.number().int().nonnegative(),
          /**
           * The sender's own step (D02): adopted rather than signed through a link, and the step a
           * `sender_order: AFTER` request waits on before its response set can seal.
           */
          is_sender: z.boolean(),
          capability_version: z.number().int().positive(),
          /**
           * The server-recorded first presentation of the document to this participant's current
           * capability (AT-017): an opened/viewed observation, not an email open and not proof of
           * identity. Absent until it happens, and again after a correction until the corrected
           * identity opens it. Both tiers and both delivery modes carry it (§2.1).
           */
          viewed_at: IsoDateTime.optional(),
          submitted_at: IsoDateTime.optional(),
          /**
           * CC final read access (RMT-12): `FINAL` only once the request is committed, `NONE`
           * before that. Present only for CC recipients — a signer's read path is its receipt and
           * the final artifact, not this field.
           */
          read_access: z.enum(['NONE', 'FINAL']).optional(),
        })
        .strict(),
    ),
    /** Sender-visible deadline facts, never a promise about email delivery. */
    deadlines: z
      .object({
        signing_deadline: IsoDateTime.optional(),
        finalization_deadline: IsoDateTime.optional(),
        download_deadline: IsoDateTime.optional(),
      })
      .strict(),
    /**
     * Manual routing affordance (RMT-07/AT-012): the external step whose capability link is
     * authorized right now, so the sender knows which link to reveal next without any managed
     * email. Absent while nothing is authorized and for managed delivery, where the service
     * queues the invitation itself. The `recipients` list carries every step's state, so a
     * parallel fan-out is readable from the same response.
     */
    next_link_ready: z
      .object({
        recipient_id: z.string().min(1).max(128),
        order_index: z.number().int().nonnegative(),
        participant_index: z.number().int().nonnegative(),
      })
      .strict()
      .optional(),
    /**
     * D10 attachment collection counts (S5.8). Counts only: the sender's read of a collected
     * file is the reviewed key-access contract, never this payload, so no filename, digest or
     * byte ever travels here. Absent when the envelope configures no attachment request.
     */
    attachments: z
      .object({
        requested: z.number().int().nonnegative(),
        collected: z.number().int().nonnegative(),
      })
      .strict()
      .optional(),
    /**
     * The request timeline (§3.6), oldest first, from server-recorded facts only. Free and
     * Lifetime alike: basic tracking is never a paid gate (§2.1). The service always sends it;
     * it is optional only so a client stays readable against a service that predates it.
     */
    timeline: z.array(RequestTimelineEvent).max(500).optional(),
    /**
     * Why the request ended, for the sender only (§6.5): the reason the signer typed when they
     * declined, or the sender's own void reason. Absent when none was given. It never travels to
     * another participant and never into diagnostics.
     */
    decline_reason: z.string().min(1).max(500).optional(),
    void_reason: z.string().min(1).max(500).optional(),
  })
  .strict()
export type EnvelopeDetailResponse = z.infer<typeof EnvelopeDetailResponse>

export const VoidEnvelopeRequest = z
  .object({
    client_request_id: ClientRequestId,
    reason: z.string().max(500).optional(),
  })
  .strict()
export type VoidEnvelopeRequest = z.infer<typeof VoidEnvelopeRequest>

/**
 * Retires a preparation that never went out, so the sender may send the same document again as a
 * new request (the web app's "Send again" after an attempt the service refused for good). A
 * compare-and-set on the service: it succeeds only while the envelope is still `PREPARING` and
 * unactivated, moves it to `PREPARATION_FAILED` and releases its reservation; an activation that
 * commits first wins and this answers `ENVELOPE_TERMINAL`. Repeating it on a retired preparation
 * answers the same success.
 */
export const DiscardPreparationRequest = z
  .object({
    client_request_id: ClientRequestId,
  })
  .strict()
export type DiscardPreparationRequest = z.infer<typeof DiscardPreparationRequest>

export const DiscardPreparationResponse = z
  .object({
    envelope_id: z.string().min(1).max(128),
    state: z.literal('PREPARATION_FAILED'),
  })
  .strict()
export type DiscardPreparationResponse = z.infer<typeof DiscardPreparationResponse>

/** Sender intent observation only — never delivery proof (OP-021, MET-15). */
export const ShareEventRequest = z
  .object({
    client_request_id: ClientRequestId,
    type: z.enum([
      'share_requested',
      'share_sheet_opened',
      'link_copied',
      'qr_shown',
      'handoff_observed',
    ]),
    channel: z.string().max(40).optional(),
    /**
     * Whose link the sender shared, copied or showed: an external signer of this envelope. The
     * audit row is attributed to that participant, so a copy of Signer 1's link never reads as
     * the sender's own. Absent records the observation against the request as a whole.
     */
    recipient_id: z.string().min(1).max(128).optional(),
  })
  .strict()
export type ShareEventRequest = z.infer<typeof ShareEventRequest>

/* ------------------------- operational edits (S5.5) ------------------------ */

/**
 * Correcting a recipient — §6.5, AT-015, STM-03/10.
 *
 * The editable set is exactly the participant's **identity**: name, email, role and locale.
 * Order, participant index, kind and the sender flag are what the sender agreed to and are
 * frozen at activation (0008), and accepted evidence is never edited — a correction rotates the
 * capability instead. `expected_capability_version` is the revision the sender read; a stale tab
 * is refused rather than silently overwriting a newer state (STM-10).
 */
export const CorrectRecipientRequest = z
  .object({
    client_request_id: ClientRequestId,
    expected_capability_version: z.number().int().positive(),
    name: z.string().min(1).max(200).optional(),
    email: Email.optional(),
    role_id: z.string().min(1).max(64).optional(),
    locale_code: LocaleCode.optional(),
    /**
     * Also hand the corrected person the new invitation in the same operation — managed queues a
     * second invitation for the same step (never another credit: AT-015), manual reveals the new
     * link. Omitted means the correction changes identity only.
     */
    resend: z.boolean().optional(),
  })
  .strict()
export type CorrectRecipientRequest = z.infer<typeof CorrectRecipientRequest>

/** The invitation budget of one participant slot, derived from the register (S4.3 values). */
export const InvitationBudget = z
  .object({
    limit: z.number().int().nonnegative(),
    used: z.number().int().nonnegative(),
    remaining: z.number().int().nonnegative(),
    /** Present when a spacing rule is the reason nothing may be sent right now. */
    next_allowed_at: IsoDateTime.optional(),
  })
  .strict()
export type InvitationBudget = z.infer<typeof InvitationBudget>

/**
 * One invitation's observable outcome. Manual reveals the capability link the sender already
 * holds and sends nothing; managed returns no link and reports the queued invitation. The two
 * shapes are distinct so a client cannot render a link the service did not hand over.
 */
export const InvitationOutcome = z
  .object({
    kind: z.enum(['manual', 'managed']),
    /** Manual only: the existing valid capability link, revealed explicitly (SEC-03 redaction). */
    link: z
      .object({
        url: z.string().min(1).max(2048),
        token: z.string().min(1).max(512),
        expires_at: IsoDateTime,
      })
      .strict()
      .optional(),
    /** Managed only: the service queued the invitation; there is nothing for the client to send. */
    queued: z.boolean(),
    budget: InvitationBudget,
  })
  .strict()
export type InvitationOutcome = z.infer<typeof InvitationOutcome>

export const CorrectRecipientResponse = z
  .object({
    envelope_id: z.string().min(1).max(128),
    recipient: z
      .object({
        recipient_id: z.string().min(1).max(128),
        kind: ParticipantKind,
        name: z.string().min(1).max(200),
        email: Email.optional(),
        role_id: z.string().min(1).max(64).optional(),
        state: RecipientState,
        order_index: z.number().int().nonnegative(),
        capability_version: z.number().int().positive(),
      })
      .strict(),
    /**
     * Stated explicitly because the client must retire the link it is holding: the previous
     * capability version can no longer be used for anything.
     */
    previous_capability_invalid: z.literal(true),
    previous_capability_version: z.number().int().positive(),
    /** Present when the correction also handed over a new invitation (`resend: true`). */
    invitation: InvitationOutcome.optional(),
  })
  .strict()
export type CorrectRecipientResponse = z.infer<typeof CorrectRecipientResponse>

/** A resend of the invitation for one slot — §6.4, §14.3. */
export const ResendInvitationRequest = z
  .object({
    client_request_id: ClientRequestId,
    expected_capability_version: z.number().int().positive(),
  })
  .strict()
export type ResendInvitationRequest = z.infer<typeof ResendInvitationRequest>

export const ResendInvitationResponse = z
  .object({
    envelope_id: z.string().min(1).max(128),
    recipient_id: z.string().min(1).max(128),
    delivery_mode: DeliveryMode,
    capability_version: z.number().int().positive(),
    invitation: InvitationOutcome,
  })
  .strict()
export type ResendInvitationResponse = z.infer<typeof ResendInvitationResponse>

/**
 * The single P0 deadline edit — §6.5, MCP-AC-128, AT-016.
 *
 * Expressed as a requested window in days from the **original activation**, which is what the
 * rule is measured against; the service refuses anything that is not longer than the current
 * deadline and anything beyond the register ceiling. `expected_revision` is the envelope's
 * operational revision (STM-10).
 */
export const ExtendExpiryRequest = z
  .object({
    client_request_id: ClientRequestId,
    expected_revision: z.number().int().nonnegative(),
    /**
     * The requested window in days from the **original activation**. The register's ceiling
     * (`lifetime_max_signing_days`) is enforced by the service as the exact
     * `limitation.signing_days` refusal; this bound only stops a nonsense payload from reaching
     * it, so the product rule keeps its one source.
     */
    requested_days: z.number().int().positive().max(365),
  })
  .strict()
export type ExtendExpiryRequest = z.infer<typeof ExtendExpiryRequest>

export const ExtendExpiryResponse = z
  .object({
    envelope_id: z.string().min(1).max(128),
    previous_expires_at: IsoDateTime,
    expires_at: IsoDateTime,
    revision: z.number().int().nonnegative(),
    /** Stated because it is the rule: an extension never moves the activation instant. */
    activation_unchanged: z.literal(true),
  })
  .strict()
export type ExtendExpiryResponse = z.infer<typeof ExtendExpiryResponse>

/**
 * A CC recipient's post-commit read authority (RMT-12). The link is a final-artifact read grant
 * and nothing else: it carries no field access and no submission authority, and it does not
 * exist before the request is committed.
 */
export const CcFinalAccessResponse = z
  .object({
    envelope_id: z.string().min(1).max(128),
    recipient_id: z.string().min(1).max(128),
    recipient_name: z.string().min(1).max(200),
    artifact_class: ArtifactClass,
    final_hash: HashString,
    url: z.string().min(1).max(2048),
    token: z.string().min(1).max(512),
    expires_at: IsoDateTime,
  })
  .strict()
export type CcFinalAccessResponse = z.infer<typeof CcFinalAccessResponse>

/**
 * What the CC recipient's page says around its one Download button (§7.5, §10.3): which agreement,
 * who shared it, and until when — the same three facts the CC email already carries. Read under the
 * same grant as the artifact, and only while it is readable.
 */
export const CcFinalSummaryResponse = z
  .object({
    document_title: z.string().min(1).max(300),
    sender_display_name: z.string().min(1).max(200),
    available_until: IsoDateTime,
  })
  .strict()
export type CcFinalSummaryResponse = z.infer<typeof CcFinalSummaryResponse>

/* -------------------------------- recipient ------------------------------- */

export const SignBootstrapResponse = z
  .object({
    envelope_id: z.string().min(1).max(128),
    recipient_id: z.string().min(1).max(128),
    recipient_name: z.string().min(1).max(200),
    /** Index into the envelope's participant list; the package's participants use the same one. */
    participant_index: z.number().int().nonnegative(),
    sender_display_name: z.string().min(1).max(200),
    document_title: z.string().min(1).max(300),
    state: EnvelopeState,
    capability_version: z.number().int().positive(),
    expires_at: IsoDateTime,
    /** The sender's plan: Free signing pages carry the brand mark, Lifetime pages do not. */
    plan: PlanTier,
    consent: z
      .object({
        version: z.string().min(1).max(40),
        locale: LocaleCode,
        text_hash: z.string().regex(/^[0-9a-f]{64}$/),
      })
      .strict(),
    /**
     * What this recipient may be shown (§6.1). `AUTHORIZED` means values the sender and earlier
     * steps are authorized to share are part of the package; `NONE` means only the original
     * frozen source. Unrelated participants' response material is never part of a recipient
     * payload in either case — the field exists so the client never has to guess.
     *
     * This is the **D01 security-review gate**: it is off (NONE) unless the deployment records
     * the reviewed outcome, and enabling it is a configuration decision, never a client one.
     */
    visibility: z
      .object({ prior_values: z.enum(['NONE', 'AUTHORIZED']) })
      .strict()
      .optional(),
    /** Client-encrypted source the recipient decrypts locally (§7.1). */
    source_grant: DownloadGrant,
    assigned_fields_digest: z.string().regex(/^[0-9a-f]{64}$/),
    /**
     * D10 attachment requests **owned by this participant** (S5.8). A request owned by another
     * signer is never in this payload, and a CC recipient holds no capability to reach it at
     * all — so the recipient page renders exactly what this person may collect (D-20). Absent
     * when the envelope configures no attachment request for this participant.
     */
    attachment_requests: z.array(AttachmentRequest).max(20).optional(),
  })
  .strict()
export type SignBootstrapResponse = z.infer<typeof SignBootstrapResponse>

export const SignEventRequest = z
  .object({
    client_request_id: ClientRequestId,
    type: z.enum(['bootstrap', 'document_presented', 'field_focused', 'validation_failed']),
    occurred_at: IsoDateTime,
    detail_code: z.string().max(60).optional(),
  })
  .strict()
export type SignEventRequest = z.infer<typeof SignEventRequest>

export const SubmitResponseRequest = z
  .object({
    client_request_id: ClientRequestId,
    capability_version: z.number().int().positive(),
    response_hash: HashString,
    response_bytes: z.number().int().positive(),
    response_grant: UploadGrant.optional(),
    consent: z
      .object({
        version: z.string().min(1).max(40),
        locale: LocaleCode,
        accepted: z.literal(true),
        displayed_text_hash: z.string().regex(/^[0-9a-f]{64}$/),
      })
      .strict(),
    adopted_at: IsoDateTime,
  })
  .strict()
export type SubmitResponseRequest = z.infer<typeof SubmitResponseRequest>

/**
 * What a signer's receipt page needs to say what happens next (§7.1, §7.5, DEV-008): which
 * agreement, from whom, whether the copy arrives by email (managed) or waits at this link
 * (manual), and how long it stays. Only a participant who already submitted reads it, and they
 * saw the title and sender at bootstrap. Optional so an older service still answers a valid receipt.
 */
export const SignerReceiptContext = z
  .object({
    document_title: z.string().min(1).max(300),
    sender_display_name: z.string().min(1).max(200),
    delivery_mode: DeliveryMode,
    plan: PlanTier,
    /** Days the signed copy stays downloadable after completion (DEV-008). */
    retention_days: z.number().int().positive(),
    completed_at: IsoDateTime.nullable(),
    /** The committed download window; null until the agreement is complete. */
    download_deadline: IsoDateTime.nullable(),
  })
  .strict()
export type SignerReceiptContext = z.infer<typeof SignerReceiptContext>

export const SubmitResponseResponse = z
  .object({
    recipient_id: z.string().min(1).max(128),
    state: RecipientState,
    envelope_state: EnvelopeState,
    receipt: z
      .object({
        receipt_id: z.string().min(1).max(128),
        server_recorded_at: IsoDateTime,
        response_digest: z.string().regex(/^[0-9a-f]{64}$/),
      })
      .strict(),
    context: SignerReceiptContext.optional(),
  })
  .strict()
export type SubmitResponseResponse = z.infer<typeof SubmitResponseResponse>

/**
 * Response ciphertext upload acknowledgement (REC-03, §7.1).
 *
 * The digest is returned in the same bare-hex form the stored object's metadata and the
 * subsequent submit use, so the client binds exactly what the service received.
 */
export const RecipientResponseUploadResponse = z
  .object({
    object_key: z.string().min(1).max(512),
    response_hash: z.string().regex(/^[0-9a-f]{64}$/),
    response_bytes: z.number().int().positive(),
  })
  .strict()
export type RecipientResponseUploadResponse = z.infer<typeof RecipientResponseUploadResponse>

export const DeclineRequest = z
  .object({
    client_request_id: ClientRequestId,
    reason: z.string().max(500).optional(),
  })
  .strict()
export type DeclineRequest = z.infer<typeof DeclineRequest>

/* --------------------- attachments (D10, S5.8) ---------------------------- */

/**
 * Grant → PUT → confirm, in that order.
 *
 * The grant names one request this participant owns and reserves one upload slot; the client
 * PUTs the **ciphertext** to `upload.url`; the confirm binds the stored object to an immutable
 * attachment. The service verifies the stored object's own digest and size against what was
 * confirmed — the client's claim is never the record — and the attachment's identity is the
 * request plus the file's digest, so retrying an interrupted upload cannot create a second
 * accepted file (D-18).
 */
export const AttachmentGrantRequest = z
  .object({
    client_request_id: ClientRequestId,
    attachment_request_id: z.string().min(1).max(128),
    /** Metadata only; the name is sanitized before it is stored (D10). */
    file_name: z.string().min(1).max(300),
    /** The browser's MIME type; mapped to the contract's file kind and checked against the request. */
    content_type: z.string().min(1).max(120),
    bytes: z.number().int().positive(),
  })
  .strict()
export type AttachmentGrantRequest = z.infer<typeof AttachmentGrantRequest>

export const AttachmentGrantResponse = z
  .object({
    /** The slot's identity; the confirm quotes it. */
    attachment_id: z.string().min(1).max(128),
    upload: UploadGrant,
  })
  .strict()
export type AttachmentGrantResponse = z.infer<typeof AttachmentGrantResponse>

export const AttachmentConfirmRequest = z
  .object({
    client_request_id: ClientRequestId,
    attachment_id: z.string().min(1).max(128),
    /** The stored ciphertext's digest (`sha256:<hex>`), verified against the object, not trusted. */
    digest: HashString,
    bytes: z.number().int().positive(),
    /** The name as the recipient sees it; sanitized again on the way in. */
    file_name: z.string().min(1).max(300),
  })
  .strict()
export type AttachmentConfirmRequest = z.infer<typeof AttachmentConfirmRequest>

export const AttachmentConfirmResponse = z
  .object({
    attachment: AttachmentManifest,
    /** The stored object's bytes were verified and the attachment is now accepted. */
    received: z.literal(true),
  })
  .strict()
export type AttachmentConfirmResponse = z.infer<typeof AttachmentConfirmResponse>

/** This participant's collected attachments — their own files only, and never any bytes (D-20). */
export const AttachmentListResponse = z
  .object({ items: z.array(AttachmentManifest).max(100) })
  .strict()
export type AttachmentListResponse = z.infer<typeof AttachmentListResponse>

/**
 * One configured request as its **sender** reads it (D-19, D-20).
 *
 * `acknowledged` is the durable-acknowledgement state the required-attachment rule is written
 * in: an accepted attachment exists for the request. `collected` counts the files, so a
 * request with room for three is readable as "one of three collected".
 */
export const AttachmentCollectionRequestState = z
  .object({
    request_id: z.string().min(1).max(128),
    field_id: z.string().min(1).max(64),
    label: z.string().min(1),
    owner: AttachmentOwner,
    required: z.boolean(),
    acknowledged: z.boolean(),
    collected: z.number().int().nonnegative(),
  })
  .strict()
export type AttachmentCollectionRequestState = z.infer<typeof AttachmentCollectionRequestState>

/** One collected file as its sender reads it: the recorded manifest, plus who submitted it. */
export const AttachmentCollectionEntry = z
  .object({
    /** The recorded manifest — read, never re-derived at read time (D-27). */
    manifest: AttachmentManifest,
    /** The participant who submitted the file; visibility defaults to uploader + sender (D10). */
    participant_id: z.string().min(1).max(128),
    /** An accepted attachment exists only once its upload was durably acknowledged (D-19). */
    acknowledged: z.literal(true),
  })
  .strict()
export type AttachmentCollectionEntry = z.infer<typeof AttachmentCollectionEntry>

/**
 * The sender's own read of one agreement's collection (S5.8, D10/D-19/D-20).
 *
 * The sender is one of the two viewers a collection defaults to, so they may read the **manifest
 * metadata** — what was requested, who submitted what, and whether a required request has been
 * acknowledged — and no byte ever travels here. Attachment **bytes** stay behind the reviewed
 * key-access read (`GET /v1/envelopes/:id/attachments/:attachment_id`), which answers
 * `OP_FORBIDDEN_ATTACHMENT_ACCESS` until the §14.7 review is recorded.
 */
export const AttachmentCollectionResponse = z
  .object({
    requests: z.array(AttachmentCollectionRequestState).max(20),
    items: z.array(AttachmentCollectionEntry).max(100),
  })
  .strict()
export type AttachmentCollectionResponse = z.infer<typeof AttachmentCollectionResponse>

/**
 * The reviewed key-access read of an attachment (D10, §14.7). Available only where the
 * deployment recorded the review (`ATTACHMENT_FINALIZER_ACCESS`); anywhere else the answer is
 * `OP_FORBIDDEN_ATTACHMENT_ACCESS` and nothing about the file is disclosed. The grant carries
 * the **ciphertext**, which the authorized viewer decrypts locally — the service never claims to
 * have inspected plaintext it cannot decrypt.
 */
export const AttachmentAccessResponse = z
  .object({
    attachment: AttachmentManifest,
    download: DownloadGrant,
  })
  .strict()
export type AttachmentAccessResponse = z.infer<typeof AttachmentAccessResponse>

/* ------------------------------ finalization ------------------------------ */

/** A session and the live lease are both required to read finalization ciphertext bytes. */
export const FINALIZE_LEASE_ID_HEADER = 'x-signbento-finalization-lease-id'
export const FINALIZE_FENCING_TOKEN_HEADER = 'x-signbento-finalization-fencing-token'

export const FinalizePrepareResponse = z
  .object({
    envelope_id: z.string().min(1).max(128),
    response_set_version: z.number().int().positive(),
    response_set_digest: z.string().regex(/^[0-9a-f]{64}$/),
    /** Exact immutable signing-source bytes and frozen field manifest declared at create. */
    source_hash: HashString,
    field_manifest_digest: z.string().regex(/^[0-9a-f]{64}$/),
    lease: z
      .object({
        lease_id: z.string().min(1).max(128),
        fencing_token: z.number().int().positive(),
        expires_at: IsoDateTime,
      })
      .strict(),
    /** The server-observed commitments needed to verify the exact ciphertext and frozen set. */
    response_grants: z
      .array(
        DownloadGrant.extend({
          recipient_id: z.string().min(1).max(128),
          response_digest: z.string().regex(/^[0-9a-f]{64}$/),
          accepted_at: IsoDateTime,
        }).strict(),
      )
      .max(20),
    source_grant: DownloadGrant,
    finalization_deadline: IsoDateTime,
  })
  .strict()
export type FinalizePrepareResponse = z.infer<typeof FinalizePrepareResponse>

export const FinalizeCommitRequest = z
  .object({
    client_request_id: ClientRequestId,
    response_set_version: z.number().int().positive(),
    lease_id: z.string().min(1).max(128),
    fencing_token: z.number().int().positive(),
    /** Exact composed PDF bytes, attested by the authorized finalizing client. */
    final_pdf_hash: HashString,
    final_pdf_bytes: z.number().int().positive(),
    /** Encrypted staged object; the service independently verifies both values. */
    ciphertext_hash: HashString,
    ciphertext_bytes: z.number().int().positive(),
    composer_version: z.string().min(1).max(64),
    artifact_grant: UploadGrant.optional(),
  })
  .strict()
export type FinalizeCommitRequest = z.infer<typeof FinalizeCommitRequest>

export const FinalizeCommitResponse = z
  .object({
    envelope_id: z.string().min(1).max(128),
    state: EnvelopeState,
    completed_at: IsoDateTime,
    final_hash: HashString,
    final_hash_authority: z.literal('CLIENT_ATTESTED'),
    completion_manifest_key: z.string().min(1).max(512),
    download_deadline: IsoDateTime,
  })
  .strict()
export type FinalizeCommitResponse = z.infer<typeof FinalizeCommitResponse>

/* ---------------------------- audit certificate ---------------------------- */

const Sha256Hex = z.string().regex(SHA256_HEX_PATTERN)

/**
 * The facts an audit certificate is rendered from — PRD §7.3 ("creates the audit certificate"),
 * §7.4, §11.3 ("Completion hash record and audit-certificate metadata"), OP-009, DIG-07, AUD-08.
 *
 * The certificate is a companion to the final PDF, never part of it: `final_pdf_sha256` is the
 * hash of the complete PDF bytes and nothing on the certificate is hashed into them (OP-009). The
 * service returns facts it holds and the client renders them, so the certificate can be
 * re-rendered without touching any committed digest.
 *
 * What is deliberately absent: IP addresses and user agents are not collected (§7.4 "Audit
 * IP/user-agent collection is Off"), so there is no field for them, and `not_collected` says so
 * instead of leaving a gap that reads like a failure (AUD-08). Participant names and email
 * addresses are what the sender supplied — claims, not identity proof (§7.4). A signer's copy
 * carries names only; email addresses appear on the sender's own copy.
 */
export const AuditCertificateParticipant = z
  .object({
    participant_index: z.number().int().nonnegative(),
    kind: ParticipantKind,
    /** The sender's own signing step (D02). */
    is_sender: z.boolean(),
    order_index: z.number().int().nonnegative(),
    name: z.string().min(1).max(200),
    /** Sender's copy only. */
    email: Email.optional(),
    /** First server-recorded presentation of the document to this participant. */
    presented_at: IsoDateTime.optional(),
    /**
     * Server receipt of this participant's accepted response, or of the sender's own adopted step
     * (D02). Absent for a CC and for anyone who did not sign.
     */
    accepted_at: IsoDateTime.optional(),
    /** The accepted response package's digest; a sender adoption has no response package. */
    response_sha256: Sha256Hex.optional(),
    /** The exact consent text version, locale and digest the response bound (AUD-01). */
    consent: z
      .object({
        version: z.string().min(1).max(40),
        locale: LocaleCode,
        text_sha256: Sha256Hex,
      })
      .strict()
      .optional(),
  })
  .strict()
export type AuditCertificateParticipant = z.infer<typeof AuditCertificateParticipant>

export const AuditCertificateEvent = z
  .object({
    sequence: z.number().int().nonnegative(),
    event_type: AuditEventType,
    /** Server-recorded instant; a device-reported time never replaces it (§7.4). */
    occurred_at: IsoDateTime,
    actor_kind: z.enum(['sender', 'recipient', 'system']),
    /** The participant an event belongs to, by index — never a recipient id or capability. */
    participant_index: z.number().int().nonnegative().nullable(),
  })
  .strict()
export type AuditCertificateEvent = z.infer<typeof AuditCertificateEvent>

export const AuditCertificateResponse = z
  .object({
    format: z.literal('signbento-audit-certificate'),
    version: z.literal(1),
    /** Whose copy this is; it decides whether email addresses are present. */
    audience: z.enum(['SENDER', 'SIGNER']),
    envelope_id: z.string().min(1).max(128),
    title: z.string().min(1).max(300),
    delivery_mode: DeliveryMode,
    routing_mode: RoutingMode,
    sender: z
      .object({
        name: z.string().min(1).max(200),
        /** Sender's copy only: the address of the signed-in account that sent the request. */
        email: Email.optional(),
      })
      .strict(),
    created_at: IsoDateTime,
    activated_at: IsoDateTime.optional(),
    completed_at: IsoDateTime,
    participants: z.array(AuditCertificateParticipant).min(1).max(20),
    completion: z
      .object({
        evidence_profile: z.string().min(1).max(64),
        source_sha256: Sha256Hex,
        field_manifest_sha256: Sha256Hex,
        response_set_version: z.number().int().positive(),
        response_set_sha256: Sha256Hex,
        final_pdf_sha256: Sha256Hex,
        final_pdf_bytes: z.number().int().positive(),
        /** The finalizing device hashed the PDF; the service verified the encrypted copy it holds. */
        pdf_hash_authority: z.literal('CLIENT_ATTESTED'),
        composer_version: z.string().min(1).max(64),
        manifest_sha256: Sha256Hex,
        audit_tip_sha256: Sha256Hex,
        /**
         * Whether the service signed the completion manifest. `signed: false` is the honest
         * record when no approved signing key was configured — never presented as signed.
         */
        attestation: z.discriminatedUnion('signed', [
          z
            .object({
              signed: z.literal(true),
              key_id: z.string().min(1).max(128),
              algorithm: z.string().min(1).max(64),
            })
            .strict(),
          z
            .object({
              signed: z.literal(false),
              reason: z.literal('no_approved_signing_key'),
            })
            .strict(),
        ]),
      })
      .strict(),
    /**
     * The stored audit chain in sequence order, including events after completion (AUD-07). The
     * bound is the one the audit read already replays a chain within.
     */
    events: z.array(AuditCertificateEvent).max(MCP_AUDIT_MAX_FOLD_EVENTS),
    not_collected: z.array(z.enum(['ip_address', 'user_agent'])),
    retention: z
      .object({
        /** The server copies of the final PDF and its evidence are kept until this instant. */
        server_copy_until: IsoDateTime,
      })
      .strict(),
    generated_at: IsoDateTime,
  })
  .strict()
export type AuditCertificateResponse = z.infer<typeof AuditCertificateResponse>

/* -------------------------------- insights -------------------------------- */

/** Rolling UTC activation cohort; the inventory cards always use the current snapshot. */
export const InsightsSummaryQuery = z
  .object({
    days: z.coerce
      .number()
      .int()
      .refine((days) => [7, 30, 90].includes(days))
      .optional(),
    /** IANA zone the activity chart's days are cut in; the service falls back to UTC. */
    tz: z
      .string()
      .regex(/^[A-Za-z0-9_+\-/]{1,64}$/)
      .optional(),
  })
  .strict()
export type InsightsSummaryQuery = z.infer<typeof InsightsSummaryQuery>

/**
 * The Insights charts' cut of the same activation cohort R07–R10 count: sends per local day (and
 * how many of those completed), the cohort by outcome, and the account's latest request events.
 * Web summary only; MCP reads the metrics.
 */
export const InsightsActivity = z
  .object({
    timezone: z.string().min(1).max(64),
    days: z
      .array(
        z
          .object({
            date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
            sent: z.number().int().nonnegative(),
            completed: z.number().int().nonnegative(),
          })
          .strict(),
      )
      .max(100),
    outcomes: z
      .object({
        completed: z.number().int().nonnegative(),
        in_progress: z.number().int().nonnegative(),
        declined: z.number().int().nonnegative(),
        expired: z.number().int().nonnegative(),
        voided: z.number().int().nonnegative(),
      })
      .strict(),
    recent: z
      .array(
        z
          .object({
            envelope_id: z.string().min(1).max(128),
            title: z.string().min(1).max(300),
            kind: z.enum([
              'sent',
              'opened',
              'signed',
              'completed',
              'declined',
              'expired',
              'voided',
            ]),
            at: IsoDateTime,
          })
          .strict(),
      )
      .max(10),
  })
  .strict()
export type InsightsActivity = z.infer<typeof InsightsActivity>

export const InsightsSummaryResponse = z
  .object({
    scope: z.literal('account'),
    as_of: IsoDateTime,
    metrics: z.array(MetricResultEnvelope).max(13),
    activity: InsightsActivity.optional(),
  })
  .strict()
export type InsightsSummaryResponse = z.infer<typeof InsightsSummaryResponse>

/* -------------------------------- purchases ------------------------------- */

/**
 * Web checkout and provider verification — PRD §2.9, §10.2, §14.8, D10; S5.2
 * (PUR-13, PUR-10, OP-023).
 *
 * The request schemas **cannot carry a card field, a CVC or a billing address** — `.strict()`
 * rejects any unknown key, so a `card_number`/`cvc`/`pan` sent by a buggy client is refused
 * before a handler runs. Card collection happens on the provider's hosted page; SignBento never
 * receives it (MCP-AC-059 analog).
 */

export const WebCheckoutSessionRequest = z
  .object({
    client_request_id: ClientRequestId,
    /** The SKU to buy. A candidate pack that is not on sale is refused, not silently substituted. */
    product_id: z.string().min(1).max(128),
  })
  .strict()
export type WebCheckoutSessionRequest = z.infer<typeof WebCheckoutSessionRequest>

/**
 * The hosted session the client opens. `reused` is `true` when an already-open session for the
 * same SKU was handed back instead of creating a second payable one (no accidental duplicate
 * charge); `url` is the provider's own hosted page.
 */
export const CheckoutSessionView = z
  .object({
    session_id: z.string().min(1).max(255),
    url: z.string().min(1).max(2048),
    expires_at: IsoDateTime,
    reused: z.boolean(),
  })
  .strict()
export type CheckoutSessionView = z.infer<typeof CheckoutSessionView>

/**
 * The display facts the checkout surface renders: the one-time price, what the money buys, and
 * the USD baseline currency. Localized prices are shown on the provider's hosted page (PUR-10).
 */
export const CheckoutProductDisplay = z
  .object({
    product_id: z.string().min(1).max(128),
    purchase_type: PurchaseType,
    price_usd: z.number().nonnegative(),
    credits: z.number().int().nonnegative(),
    currency: z.literal('USD'),
  })
  .strict()
export type CheckoutProductDisplay = z.infer<typeof CheckoutProductDisplay>

export const WebCheckoutSessionResponse = z
  .object({
    session: CheckoutSessionView,
    product: CheckoutProductDisplay,
  })
  .strict()
export type WebCheckoutSessionResponse = z.infer<typeof WebCheckoutSessionResponse>

/**
 * Whether this deployment can open a web checkout at all. Public and content-free: it says only
 * whether a payment provider is composed, so the web app can hide purchase entry points that would
 * otherwise fail closed with `OP_CONFIGURATION_GATE_UNSET`. Turning purchases on stays deployment
 * config — set the provider's values and this answers `true`.
 */
export const PurchaseAvailabilityResponse = z
  .object({
    web_checkout: z.boolean(),
    /**
     * Whether this deployment can verify App Store / Google Play purchases. The installed app
     * offers a store purchase only when its own store billing works on the device **and** the
     * service says that store's verification is configured. Optional only so an older service's
     * answer still parses (absent means no).
     */
    apple: z.boolean().optional(),
    google_play: z.boolean().optional(),
  })
  .strict()
export type PurchaseAvailabilityResponse = z.infer<typeof PurchaseAvailabilityResponse>

/**
 * Explicit verification for a client return flow. A redirect success on its own grants nothing:
 * the service re-derives from the provider's current state and only then applies a verified
 * purchase. `state` is `PENDING` while the provider has not confirmed.
 */
export const VerifyPurchaseRequest = z
  .object({
    client_request_id: ClientRequestId,
    /** Narrow verification to one provider session; omitted means every open session for the account. */
    session_id: z.string().min(1).max(255).optional(),
  })
  .strict()
export type VerifyPurchaseRequest = z.infer<typeof VerifyPurchaseRequest>

export const PurchaseVerificationState = z.enum([
  'PENDING',
  'PURCHASED',
  'REVOKED',
  'NOTHING_TO_VERIFY',
])
export type PurchaseVerificationState = z.infer<typeof PurchaseVerificationState>

export const VerifyPurchaseResponse = z
  .object({
    state: PurchaseVerificationState,
    /** The same account snapshot `/v1/me` answers, so the client never re-derives a balance. */
    entitlement: MeEntitlementSnapshot,
  })
  .strict()
export type VerifyPurchaseResponse = z.infer<typeof VerifyPurchaseResponse>

/* ------------------------- native store purchases ------------------------- */

/**
 * Native store billing — PRD §2.9, §10.2, §14.8 (PUR-01/02/05/10, store rules §10.2: App Store on
 * iOS/iPadOS, Play Billing on Android, Stripe on the web).
 *
 * The app hands the service exactly what the store signed and nothing else: Apple's signed
 * transaction (the StoreKit 2 `jwsRepresentation`, verified to Apple's root on the service) or
 * Google Play's purchase token (re-read from the Play Developer API with the service's own
 * credential). Neither is trusted on the device's word, neither is logged, and neither carries a
 * payment credential — the store's own sheet collected that.
 */
const CompactJws = z
  .string()
  .min(16)
  .max(32_768)
  .regex(/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/)

export const StorePurchaseRequest = z.discriminatedUnion('provider', [
  z
    .object({
      client_request_id: ClientRequestId,
      provider: z.literal('apple'),
      signed_transaction: CompactJws,
    })
    .strict(),
  z
    .object({
      client_request_id: ClientRequestId,
      provider: z.literal('google_play'),
      product_id: z.string().min(1).max(128),
      purchase_token: z.string().min(1).max(4096),
    })
    .strict(),
])
export type StorePurchaseRequest = z.infer<typeof StorePurchaseRequest>

/**
 * `PURCHASED` — applied now. `ALREADY_APPLIED` — this account already holds it (a restore or a
 * retry; no second grant). `PENDING` — the store has not taken the money yet (Play's pending
 * payment, Ask to Buy); nothing is unlocked. `REVOKED` — the store refunded or revoked it.
 */
export const StorePurchaseState = z.enum(['PURCHASED', 'ALREADY_APPLIED', 'PENDING', 'REVOKED'])
export type StorePurchaseState = z.infer<typeof StorePurchaseState>

export const StorePurchaseResponse = z
  .object({
    state: StorePurchaseState,
    product_id: z.string().min(1).max(128),
    purchase_type: PurchaseType,
    /** Credits this call added (0 for a restore, a replay or a duplicate Lifetime purchase). */
    granted_credits: z.number().int().nonnegative(),
    /**
     * True once the service holds this purchase on its records, so the app may finish the store
     * transaction (StoreKit `finish()`, Play acknowledge/consume). False while it is pending: an
     * unfinished transaction is the store's way of delivering it again.
     */
    finish_transaction: z.boolean(),
    entitlement: MeEntitlementSnapshot,
  })
  .strict()
export type StorePurchaseResponse = z.infer<typeof StorePurchaseResponse>

/**
 * The account's store purchase token: a random UUID the app passes to the purchase sheet
 * (`appAccountToken` / `obfuscatedAccountId`) so the signed transaction names the account it was
 * bought for. It identifies nothing outside SignBento.
 */
export const StoreAccountResponse = z
  .object({
    app_account_token: z.uuid(),
  })
  .strict()
export type StoreAccountResponse = z.infer<typeof StoreAccountResponse>

/* ---------------------- checkout context (guest handoff) ------------------- */

/**
 * The §12.6 checkout context as a handoff that spans authentication — S5.3 (§2.7/§2.8,
 * IDN-10, PUR-16, OP-022, ADD-050/051).
 *
 * Buying must never cost the person their prepared work. The context is the small record that
 * makes that true — which draft, which product, which screen they came from, what to do on
 * return, and what the provider last said — and nothing else. It carries no payment credential,
 * no card field, no provider secret and no draft content: `draft_id` is a local **reference**.
 *
 * A person who is not signed in yet can prepare all of it. The service then hands back an opaque
 * **continuation** which is the only way to claim that context afterwards, and it stays out of
 * URL query strings: it is a handle, not a payload, it is single-use in the sense that matters
 * (one account may claim it, and a resolution consumes it) and it expires with the context.
 *
 * `.strict()` everywhere, so a buggy client that tries to put a card field on either request is
 * refused before a handler runs — the same posture as the checkout request beside it.
 */

/** An app-local route only: the service refuses anything that could redirect off-app. */
const LocalReturnRoute = z
  .string()
  .min(1)
  .max(200)
  .refine(
    (route) =>
      route.startsWith('/') &&
      !route.startsWith('//') &&
      !route.includes('\\') &&
      !route.includes('\n'),
    { message: 'route must be an app-local path' },
  )

export const CheckoutContextStartRequest = z
  .object({
    /** The SKU the person intends to buy. The service owns whether it is on sale. */
    product_id: z.string().min(1).max(128),
    /** The local draft this handoff belongs to. An id, never content; `null` when there is none. */
    draft_id: z.string().min(1).max(128).nullable().optional(),
    /** Free-text screen id for support and diagnostics. */
    source_screen: z.string().min(1).max(120),
    return_route: LocalReturnRoute,
    return_action: CheckoutReturnAction,
  })
  .strict()
export type CheckoutContextStartRequest = z.infer<typeof CheckoutContextStartRequest>

/**
 * The context as the service reports it. No account identity, no provider secret, no payment
 * fact — and no continuation: the handle is returned once, beside this view. It deliberately
 * carries no reconciliation class either: a class is what a *return* answers (see
 * `CheckoutResumeResponse`), and a freshly started handoff has nothing to reconcile yet.
 */
export const CheckoutContextView = z
  .object({
    context_id: z.string().min(1).max(128),
    draft_id: z.string().min(1).max(128).nullable(),
    product_id: z.string().min(1).max(128),
    purchase_type: PurchaseType,
    source_screen: z.string().min(1).max(120),
    return_route: z.string().min(1).max(200),
    return_action: CheckoutReturnAction,
    /** What the provider last reported, as the service derived it. */
    provider_state: CheckoutProviderState,
    created_at: IsoDateTime,
    expires_at: IsoDateTime,
  })
  .strict()
export type CheckoutContextView = z.infer<typeof CheckoutContextView>

export const CheckoutContextStartResponse = z
  .object({
    context: CheckoutContextView,
    /**
     * The opaque, single-use, short-lived handle for this context. Presented as a field so it can
     * live in a POST body or the client's own store — never in a query string (§2.8).
     */
    continuation: z.string().min(1).max(512),
    /**
     * `true` when the account already had a context for this product and the same one was handed
     * back instead of a second conversion surface being created (ADD-051).
     */
    reused: z.boolean(),
  })
  .strict()
export type CheckoutContextStartResponse = z.infer<typeof CheckoutContextStartResponse>

export const CheckoutResumeRequest = z
  .object({
    client_request_id: ClientRequestId,
    continuation: z.string().min(1).max(512),
  })
  .strict()
export type CheckoutResumeRequest = z.infer<typeof CheckoutResumeRequest>

/**
 * What a resumed handoff asks the client to do. `revalidate_entitlement` is the post-purchase
 * rule: a draft prepared under a different plan is never blind-committed, and the tier guards
 * refuse an action the plan still does not permit until the entitlement really changed.
 */
export const CheckoutResumePlan = z
  .object({
    return_route: z.string().min(1).max(200),
    return_action: CheckoutReturnAction,
    revalidate_entitlement: z.boolean(),
    fresh_review_required: z.boolean(),
    open_draft_unchanged: z.literal(true),
  })
  .strict()
export type CheckoutResumePlan = z.infer<typeof CheckoutResumePlan>

export const CheckoutResumeResponse = z
  .object({
    state: CheckoutReconciliationState,
    /** Present exactly when `state` is `UNRESOLVED`, naming why nothing is claimed. */
    reason: CheckoutUnresolvedReason.optional(),
    context: CheckoutContextView,
    resume: CheckoutResumePlan,
    /** The same account snapshot `/v1/me` answers — the entitlement, re-derived on return. */
    entitlement: MeEntitlementSnapshot,
  })
  .strict()
export type CheckoutResumeResponse = z.infer<typeof CheckoutResumeResponse>

/* ---------------------- account lifecycle (S6.3) -------------------------- */

/**
 * Purchase restore, claim reconciliation, account export, account switching and account deletion
 * — PRD §2.7, §10.6, §10.8, §13.5, §14.2; S6.3 (PUR-04/05/15, RET-05, LOC-08, AT-022, ADD-007).
 *
 * Three rules are stated as data here rather than left to copy:
 *
 * 1. **A restore recovers account rights, never local bytes** — `RestoreReconciliation` carries
 *    `grants_new_entitlement: false` and `restores_local_documents: false` as literals.
 * 2. **A claim that another account already owns leaks nothing** — the reconciliation answer says
 *    only whether the matched record belongs to the calling account; never who else's it is, when
 *    it was bought, or what it was.
 * 3. **Remote scope is stated, not implied** — the export and the deletion receipt both carry
 *    explicit literals saying local files and the person's own backups are outside remote scope.
 *    A response that stayed silent would let a surface imply a device was wiped when it was not.
 */

/**
 * What a presented purchase claim reconciles to (PUR-05). `CLAIMED_BY_ANOTHER_ACCOUNT` is the
 * only member that means "this transaction exists and is not yours"; it deliberately carries no
 * identity, no amount and no provider receipt — the explicit reconciliation path is
 * `MOVE_TO_CLAIMING_ACCOUNT requires reauth_and_confirmation`.
 */
export const PurchaseClaimState = z.enum([
  'RECORDED_ON_THIS_ACCOUNT',
  'CLAIMED_BY_ANOTHER_ACCOUNT',
  'NOT_RECORDED',
  'NOT_CONFIRMED',
])
export type PurchaseClaimState = z.infer<typeof PurchaseClaimState>

/**
 * A claim names the provider transaction (a store receipt) or the provider session the checkout
 * returned. At least one locator is required: a claim with neither could not be looked up, and
 * answering it would be a guess.
 */
export const ReconcilePurchaseClaimRequest = z
  .object({
    client_request_id: ClientRequestId,
    provider_transaction_id: z.string().min(1).max(255).optional(),
    session_id: z.string().min(1).max(255).optional(),
  })
  .strict()
  .refine(
    (claim) => claim.provider_transaction_id !== undefined || claim.session_id !== undefined,
    { message: 'a claim names the provider transaction or the provider session' },
  )
export type ReconcilePurchaseClaimRequest = z.infer<typeof ReconcilePurchaseClaimRequest>

export const ReconcilePurchaseClaimResponse = z
  .object({
    state: PurchaseClaimState,
    /** Whether the matched record belongs to the calling account. Never *whose* it is otherwise. */
    owned_by_current_account: z.boolean(),
    /**
     * The explicit move, when the claim is another account's: the person signs in as that account
     * and confirms there. Present exactly for `CLAIMED_BY_ANOTHER_ACCOUNT`.
     */
    requires: z.literal('reauth_and_confirmation').optional(),
    /** A claim never mints a bundle; the entitlement it could belong to is already on record. */
    grants_new_entitlement: z.literal(false),
    /**
     * `false` when no provider adapter is wired, in which case the answer is built only from the
     * service's own records and says so instead of implying a provider round-trip it did not make.
     */
    provider_checked: z.boolean(),
    /** The calling account's own entitlement, re-derived and unchanged by someone else's claim. */
    entitlement: MeEntitlementSnapshot,
  })
  .strict()
  .superRefine((answer, ctx) => {
    if ((answer.state === 'CLAIMED_BY_ANOTHER_ACCOUNT') !== (answer.requires !== undefined)) {
      ctx.addIssue({
        code: 'custom',
        message: 'requires is present exactly when the claim is another account\u2019s',
        path: ['requires'],
      })
    }
    if (answer.state === 'CLAIMED_BY_ANOTHER_ACCOUNT' && answer.owned_by_current_account) {
      ctx.addIssue({
        code: 'custom',
        message: 'a claim owned by the calling account is never reported as another account\u2019s',
        path: ['owned_by_current_account'],
      })
    }
  })
export type ReconcilePurchaseClaimResponse = z.infer<typeof ReconcilePurchaseClaimResponse>

/** The account-scoped local state a switch must clear; named here so both halves cannot drift. */
export const AccountScopedLocalKey = z.enum([
  'auth.pending_intent',
  'account.tokens',
  'account.summary',
  'account.entitlement_cache',
  'account.host_grants',
  'account.last_verified_at',
])
export type AccountScopedLocalKey = z.infer<typeof AccountScopedLocalKey>

/** What the service enforces when the identity changes (S6.3, LOC-08, §2.7). */
export const AccountSwitchEnforcement = z.enum([
  'SESSIONS_REVOKED',
  'UNCOMMITTED_ACTIONS_CANCELLED',
  'OLD_TOKENS_REJECTED',
])
export type AccountSwitchEnforcement = z.infer<typeof AccountSwitchEnforcement>

/**
 * What only the client can do — stated, because the vault is the client's and the service can
 * neither read it nor attach it. The service never attaches a vault to an account; it can only
 * refuse to speak for one.
 */
export const AccountSwitchClientResponsibility = z.enum([
  'CLEAR_ACCOUNT_SCOPED_LOCAL_STATE',
  'KEEP_DEVICE_LOCAL_VAULT',
  'ATTACH_EXISTING_VAULT_EXPLICITLY',
])
export type AccountSwitchClientResponsibility = z.infer<typeof AccountSwitchClientResponsibility>

export const AccountSwitchRequest = z.object({ client_request_id: ClientRequestId }).strict()
export type AccountSwitchRequest = z.infer<typeof AccountSwitchRequest>

export const AccountSwitchResponse = z
  .object({
    switched_at: IsoDateTime,
    /** Uncommitted work under the old identity that the service cancelled, by class. */
    cancelled: z
      .object({
        checkout_sessions: z.number().int().nonnegative(),
        checkout_contexts: z.number().int().nonnegative(),
        host_handoffs: z.number().int().nonnegative(),
        host_approvals: z.number().int().nonnegative(),
      })
      .strict(),
    /** Sessions of the old identity this device held; a switch here is not a global sign-out. */
    sessions_revoked: z.number().int().nonnegative(),
    enforced_by_service: z.array(AccountSwitchEnforcement),
    client_responsibility: z.array(AccountSwitchClientResponsibility),
    /** The client clears these; the service names them and never writes to the vault itself. */
    clear_local_keys: z.array(AccountScopedLocalKey),
    /** The vault is never attached by the service — a truth, not a promise to be checked. */
    vault_attached_by_service: z.literal(false),
    local_records_touched: z.literal(false),
  })
  .strict()
export type AccountSwitchResponse = z.infer<typeof AccountSwitchResponse>

/* --------------------------------- export --------------------------------- */

/** The account/remote sections an export covers (§13.5 "Export and delete account"). */
export const AccountExportSection = z.enum([
  'ACCOUNT',
  'IDENTITIES',
  'ENTITLEMENT_AND_CREDITS',
  'PURCHASES',
  'REQUESTS',
  'RECIPIENTS',
  'CONNECTED_HOSTS',
  'WORKSPACE_ITEMS',
  'TRIAL',
])
export type AccountExportSection = z.infer<typeof AccountExportSection>

export const AccountExportExportItem = z
  .object({
    export_id: z.string().min(1).max(128),
    sections: z.array(AccountExportSection),
    reauthenticated_at: IsoDateTime,
  })
  .strict()
export type AccountExportExportItem = z.infer<typeof AccountExportExportItem>

export const AccountExportIdentity = z
  .object({
    provider: z.enum(['apple', 'google', 'email']),
    email: Email.nullable(),
    email_verified: z.boolean(),
    linked_at: IsoDateTime,
    revocation_state: z.enum(['ACTIVE', 'REVOKED', 'SUSPENDED']),
    revocation_reason: z.string().max(120).nullable(),
    deletion_state: z.enum(['NOT_REQUESTED', 'REQUESTED', 'COMPLETED']),
  })
  .strict()
export type AccountExportIdentity = z.infer<typeof AccountExportIdentity>

export const AccountExportPurchase = z
  .object({
    provider: PurchaseProvider,
    environment: z.enum(['sandbox', 'production']),
    provider_transaction_id: z.string().min(1).max(255),
    product_id: z.string().min(1).max(128),
    purchase_type: PurchaseType,
    state: z.enum(['pending', 'purchased', 'restored', 'revoked', 'failed']),
    verification_state: z.enum(['unverified', 'verified', 'failed']),
    purchased_at: IsoDateTime.nullable(),
    verified_at: IsoDateTime.nullable(),
    revoked_at: IsoDateTime.nullable(),
  })
  .strict()
export type AccountExportPurchase = z.infer<typeof AccountExportPurchase>

export const AccountExportCreditEntry = z
  .object({
    entry_id: z.string().min(1).max(128),
    reason: z.string().min(1).max(64),
    delta: z.number().int(),
    posted_at: IsoDateTime,
    envelope_id: z.string().max(128).nullable(),
    grant_id: z.string().max(128).nullable(),
    reverses_entry_id: z.string().max(128).nullable(),
  })
  .strict()
export type AccountExportCreditEntry = z.infer<typeof AccountExportCreditEntry>

/** One participant of an exported request: the facts the sender's own surface already shows. */
export const AccountExportParticipantFacts = z
  .object({
    role: z.string().min(1).max(120),
    state: RecipientState,
    submitted_at: IsoDateTime.nullable(),
    declined_at: IsoDateTime.nullable(),
  })
  .strict()
export type AccountExportParticipantFacts = z.infer<typeof AccountExportParticipantFacts>

export const AccountExportRequest = z
  .object({
    envelope_id: z.string().min(1).max(128),
    title: z.string().max(300),
    state: EnvelopeState,
    delivery_mode: DeliveryMode,
    routing_mode: z.string().min(1).max(64),
    created_at: IsoDateTime,
    activated_at: IsoDateTime.nullable(),
    completed_at: IsoDateTime.nullable(),
    /** Recipients and their progression — the participant facts the sender already sees. */
    participants: z.array(AccountExportParticipantFacts),
  })
  .strict()
export type AccountExportRequest = z.infer<typeof AccountExportRequest>

export const AccountExportHostConnection = z
  .object({
    grant_id: z.string().min(1).max(128),
    host_client_name: z.string().min(1).max(200),
    host_distribution: z.string().min(1).max(64),
    state: z.enum(['PENDING_CONSENT', 'ACTIVE', 'REVOKED', 'EXPIRED']),
    scopes: z.array(z.string().min(1).max(64)),
    item_scope_mode: z.enum(['ALL_PUBLISHED', 'SELECTED_ITEMS']),
    item_ids: z.array(z.string().min(1).max(128)),
    granted_at: IsoDateTime,
    state_changed_at: IsoDateTime.nullable(),
    last_used_at: IsoDateTime.nullable(),
  })
  .strict()
export type AccountExportHostConnection = z.infer<typeof AccountExportHostConnection>

export const AccountExportWorkspaceItem = z
  .object({
    item_id: z.string().min(1).max(128),
    title: z.string().max(300),
    publication_state: z.string().min(1).max(64),
    versions: z.number().int().nonnegative(),
    created_at: IsoDateTime,
    updated_at: IsoDateTime,
    /** A deleted item's purge deadline, when it is tombstoned but its bytes are not gone yet. */
    purge_due_at: IsoDateTime.nullable(),
  })
  .strict()
export type AccountExportWorkspaceItem = z.infer<typeof AccountExportWorkspaceItem>

/**
 * The scope statement every export carries. `local_files_outside_remote_scope` and
 * `user_backups_outside_remote_scope` are literals rather than copy so a surface cannot render an
 * export as "your data" and imply the device was included.
 */
export const AccountExportScope = z
  .object({
    includes: z.array(AccountExportSection),
    local_files_outside_remote_scope: z.literal(true),
    user_backups_outside_remote_scope: z.literal(true),
  })
  .strict()
export type AccountExportScope = z.infer<typeof AccountExportScope>

export const AccountExportResponse = z
  .object({
    export_version: z.literal(1),
    export: AccountExportExportItem,
    generated_at: IsoDateTime,
    scope: AccountExportScope,
    /**
     * Where the export stopped, so a bounded answer is never mistaken for a complete one: a
     * section that hit its limit says so instead of quietly omitting rows.
     */
    bounds: z
      .object({
        requests_limit: z.number().int().positive(),
        workspace_items_limit: z.number().int().positive(),
        requests_truncated: z.boolean(),
        workspace_items_truncated: z.boolean(),
      })
      .strict(),
    account: z
      .object({
        user_id: z.string().min(1).max(128),
        primary_email_normalized: Email.nullable(),
        plan: PlanTier,
        email_verified: z.boolean(),
        created_at: IsoDateTime,
      })
      .strict(),
    entitlement: MeEntitlementSnapshot,
    trial: z.enum(['AVAILABLE', 'RESERVED', 'CONSUMED']),
    identities: z.array(AccountExportIdentity),
    purchases: z.array(AccountExportPurchase),
    credit_entries: z.array(AccountExportCreditEntry),
    requests: z.array(AccountExportRequest),
    connected_hosts: z.array(AccountExportHostConnection),
    workspace_items: z.array(AccountExportWorkspaceItem),
  })
  .strict()
export type AccountExportResponse = z.infer<typeof AccountExportResponse>

/* -------------------------------- deletion -------------------------------- */

/** What is kept after deletion and why (RET-05). Classified, never spelled as copy. */
export const AccountDeletionRetentionClass = z.enum([
  /** Financial records: tax and refund obligations outlive the account. */
  'FINANCIAL_RECORDS',
  /** Minimized security and audit evidence, for its stated retention window. */
  'MINIMIZED_SECURITY_EVIDENCE',
  /** Requests that were sent: their recipients' records follow the request's own lifecycle. */
  'SENT_REQUESTS',
])
export type AccountDeletionRetentionClass = z.infer<typeof AccountDeletionRetentionClass>

export const AccountDeletionRetention = z
  .object({
    retention_class: AccountDeletionRetentionClass,
    /** A machine reason (a registry-style code), never a person-facing sentence. */
    reason: z.string().min(1).max(120),
    /** Days, when the window is bounded and known; `null` when it follows the record's lifecycle. */
    window_days: z.number().int().positive().nullable(),
  })
  .strict()
export type AccountDeletionRetention = z.infer<typeof AccountDeletionRetention>

/**
 * What deletion did, by class — the honest counterpart of the plan's disposition list. Every
 * count is what actually happened in this request (`meta.changes`), not what was expected.
 */
export const AccountDeletionDisposition = z
  .object({
    stopped_new_actions: z.literal(true),
    sessions_revoked: z.number().int().nonnegative(),
    host_grants_revoked: z.number().int().nonnegative(),
    active_requests_voided: z.number().int().nonnegative(),
    sealed_response_sets_preserved: z.number().int().nonnegative(),
    host_handoffs_cancelled: z.number().int().nonnegative(),
    host_approvals_invalidated: z.number().int().nonnegative(),
    workspace_items_deleted: z.number().int().nonnegative(),
    workspace_items_purged: z.number().int().nonnegative(),
    /** Purges the object store had not confirmed; the monitored sweep retries them (§14.2). */
    workspace_purges_pending: z.number().int().nonnegative(),
    workspace_index_versions_deleted: z.number().int().nonnegative(),
  })
  .strict()
export type AccountDeletionDisposition = z.infer<typeof AccountDeletionDisposition>

/**
 * The person types the word (store requirement): an accidental tap can never delete an account.
 * `confirmation` is a literal, so a client cannot invent a softer confirmation value.
 */
export const DeleteAccountRequest = z
  .object({
    client_request_id: ClientRequestId,
    confirmation: z.literal('DELETE'),
    /**
     * A retry of an already-completed deletion presents the receipt it was given, because the
     * session that authorized the first attempt is revoked by the deletion itself. Possession of
     * the receipt is what makes the replay a read rather than a second purge.
     */
    receipt_id: z.string().min(1).max(512).optional(),
  })
  .strict()
export type DeleteAccountRequest = z.infer<typeof DeleteAccountRequest>

/**
 * What a deletion would do to the account's requests, read before the person confirms (RET-05).
 * The numbers are the ones the deletion itself acts on: `active_request_count` requests are voided,
 * and every other request the account owns keeps its sealed response set through its own lifecycle
 * (`sealed_request_count`) — the same split the receipt reports as `active_requests_voided` and
 * `sealed_response_sets_preserved`.
 */
export const AccountDeletionPreview = z
  .object({
    active_request_count: z.number().int().nonnegative(),
    sealed_request_count: z.number().int().nonnegative(),
  })
  .strict()
export type AccountDeletionPreview = z.infer<typeof AccountDeletionPreview>

export const AccountDeletionReceipt = z
  .object({
    state: z.literal('COMPLETED'),
    /** Opaque handle for a retry; the only thing a later call needs to replay this receipt. */
    receipt_id: z.string().min(1).max(512),
    deleted_at: IsoDateTime,
    /** The session freshness proof (or the web code's verification) that authorized the deletion. */
    reauthenticated_at: IsoDateTime,
    requested_via: z.enum(['APP', 'WEB', 'INACTIVITY']),
    disposition: AccountDeletionDisposition,
    /**
     * The whole retention disclosure, in the registry's vocabulary: what is kept, why, and for how
     * long. A surface renders this; the person is never left inferring that everything is gone.
     */
    retention: z.array(AccountDeletionRetention),
    outside_remote_erasure: z
      .object({
        local_files: z.literal(true),
        user_backups: z.literal(true),
      })
      .strict(),
    /** `true` when this answer replays a deletion that had already completed. */
    idempotent_replay: z.boolean(),
  })
  .strict()
export type AccountDeletionReceipt = z.infer<typeof AccountDeletionReceipt>
